/**
 * Security utilities for sanitizing user input and preventing SQL injection attacks
 * 
 * CRITICAL: These functions are used throughout the application to prevent SQL injection.
 * Any changes must be thoroughly tested for security implications.
 */

/**
 * Sanitize search input to prevent SQL injection attacks
 * Removes dangerous SQL characters, keywords, and control characters
 * 
 * @param input - Raw user input to sanitize
 * @returns Sanitized string safe for use in SQL queries
 */
export function sanitizeSearchInput(input: string): string {
  if (!input || typeof input !== 'string') {
    return '';
  }
  
  // Remove dangerous SQL injection characters and patterns
  let sanitized = input
    .trim()
    // Remove SQL special characters that could be used for injection
    .replace(/['";%_\\]/g, '') // Single quotes, double quotes, semicolons, wildcards, backslashes
    .replace(/--/g, '') // SQL line comments
    .replace(/\/\*/g, '') // SQL block comment start
    .replace(/\*\//g, '') // SQL block comment end
    .replace(/\x00/g, '') // NULL bytes
    .replace(/\x1a/g, '') // EOF character
    .trim();
  
  // Remove SQL keywords that could be used for injection (case-insensitive)
  const sqlKeywords = [
    'UNION', 'SELECT', 'INSERT', 'UPDATE', 'DELETE', 'DROP', 'CREATE', 
    'ALTER', 'EXEC', 'EXECUTE', 'SCRIPT', 'JAVASCRIPT', 'VBSCRIPT',
    'ONLOAD', 'ONERROR', 'ONCLICK', 'CAST', 'CONVERT', 'TABLE',
    'FROM', 'WHERE', 'JOIN', 'HAVING', 'GROUP BY', 'ORDER BY',
    'WAITFOR', 'DELAY', 'BENCHMARK', 'SLEEP', 'SHUTDOWN',
    'GRANT', 'REVOKE', 'DENY', 'ATTACH', 'DETACH'
  ];
  
  sqlKeywords.forEach(keyword => {
    // Use word boundaries to only match complete words
    const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
    sanitized = sanitized.replace(regex, '');
  });
  
  // Remove any remaining control characters (0x00-0x1F, 0x7F)
  sanitized = sanitized.replace(/[\x00-\x1F\x7F]/g, '');
  
  // Remove multiple spaces and normalize whitespace
  sanitized = sanitized.replace(/\s+/g, ' ').trim();
  
  // Limit length to prevent buffer overflow attacks
  const maxLength = 100;
  return sanitized.substring(0, maxLength).trim();
}

/**
 * Sanitize email input specifically for email searches
 * More permissive than general search but still prevents SQL injection
 * 
 * @param email - Email address to sanitize
 * @returns Sanitized email safe for SQL queries
 */
export function sanitizeEmailInput(email: string): string {
  if (!email || typeof email !== 'string') {
    return '';
  }
  
  // Email-specific sanitization (allows @ and . but removes SQL injection chars)
  return email
    .trim()
    .toLowerCase()
    .replace(/['";%_\\]/g, '') // Remove SQL injection characters
    .replace(/--/g, '') // Remove SQL comments
    .replace(/\/\*/g, '') // Remove block comments
    .replace(/\*\//g, '')
    .replace(/[\x00-\x1F\x7F]/g, '') // Remove control characters
    .substring(0, 254); // Max email length per RFC
}

/**
 * Sanitize numeric input to ensure it's a valid number
 * Prevents SQL injection through numeric fields
 * 
 * @param input - Numeric input to sanitize
 * @param defaultValue - Default value if input is invalid
 * @returns Sanitized number
 */
export function sanitizeNumericInput(input: any, defaultValue: number = 0): number {
  const num = parseInt(String(input), 10);
  return isNaN(num) ? defaultValue : num;
}

/**
 * Sanitize array input for SQL IN clauses
 * Ensures all array elements are properly sanitized
 * 
 * @param arr - Array to sanitize
 * @param sanitizer - Function to sanitize each element
 * @returns Sanitized array
 */
export function sanitizeArrayInput<T>(
  arr: T[], 
  sanitizer: (item: T) => T
): T[] {
  if (!Array.isArray(arr)) {
    return [];
  }
  
  return arr
    .filter(item => item !== null && item !== undefined)
    .map(sanitizer)
    .filter(item => item !== ''); // Remove empty strings after sanitization
}

/**
 * Sanitize UUID input to ensure it's a valid UUID v4
 * Prevents SQL injection through ID fields
 * 
 * @param uuid - UUID to validate and sanitize
 * @returns Sanitized UUID or empty string if invalid
 */
export function sanitizeUUID(uuid: string): string {
  if (!uuid || typeof uuid !== 'string') {
    return '';
  }
  
  // UUID v4 regex pattern
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  
  const trimmed = uuid.trim().toLowerCase();
  return uuidRegex.test(trimmed) ? trimmed : '';
}

/**
 * Sanitize boolean input
 * Ensures boolean values are properly formatted for SQL
 * 
 * @param input - Boolean input to sanitize
 * @param defaultValue - Default value if input is invalid
 * @returns Boolean value
 */
export function sanitizeBooleanInput(input: any, defaultValue: boolean = false): boolean {
  if (typeof input === 'boolean') {
    return input;
  }
  
  if (typeof input === 'string') {
    const lower = input.toLowerCase().trim();
    if (lower === 'true' || lower === '1' || lower === 'yes') {
      return true;
    }
    if (lower === 'false' || lower === '0' || lower === 'no') {
      return false;
    }
  }
  
  if (typeof input === 'number') {
    return input !== 0;
  }
  
  return defaultValue;
}

/**
 * Escape special characters for LIKE/ILIKE queries
 * This is for when you need to search for literal %, _, or \ characters
 * 
 * @param input - String to escape
 * @returns Escaped string for LIKE queries
 */
export function escapeLikePattern(input: string): string {
  if (!input || typeof input !== 'string') {
    return '';
  }
  
  // First sanitize the input
  const sanitized = sanitizeSearchInput(input);
  
  // Then escape LIKE special characters
  return sanitized
    .replace(/\\/g, '\\\\') // Escape backslashes first
    .replace(/%/g, '\\%')   // Escape percent signs
    .replace(/_/g, '\\_');  // Escape underscores
}

/**
 * Validate and sanitize JSON input
 * Prevents JSON injection attacks
 * 
 * @param jsonString - JSON string to validate
 * @returns Parsed JSON object or null if invalid
 */
export function sanitizeJSON(jsonString: string): any | null {
  if (!jsonString || typeof jsonString !== 'string') {
    return null;
  }
  
  try {
    // Remove any potentially dangerous characters before parsing
    const cleaned = jsonString
      .replace(/[\x00-\x1F\x7F]/g, '') // Remove control characters
      .trim();
    
    const parsed = JSON.parse(cleaned);
    
    // Additional validation can be added here based on expected structure
    return parsed;
  } catch (error) {
    console.error('Invalid JSON input:', error);
    return null;
  }
}

/**
 * Create a safe SQL identifier (table/column name)
 * Only allows alphanumeric characters and underscores
 * 
 * @param identifier - Identifier to sanitize
 * @returns Safe identifier or empty string if invalid
 */
export function sanitizeSQLIdentifier(identifier: string): string {
  if (!identifier || typeof identifier !== 'string') {
    return '';
  }
  
  // Only allow alphanumeric characters and underscores
  const sanitized = identifier.replace(/[^a-zA-Z0-9_]/g, '');
  
  // Ensure it doesn't start with a number
  if (/^\d/.test(sanitized)) {
    return '';
  }
  
  // Limit length
  return sanitized.substring(0, 63); // PostgreSQL identifier limit
}

/**
 * Log potential SQL injection attempts for security monitoring
 * 
 * @param input - Original input that was flagged
 * @param context - Context where the attempt occurred
 */
export function logSQLInjectionAttempt(input: string, context: string): void {
  // In production, this should log to a security monitoring service
  console.warn('Potential SQL injection attempt detected:', {
    timestamp: new Date().toISOString(),
    context,
    input: input.substring(0, 100), // Limit logged input length
    inputLength: input.length
  });
}

/**
 * Comprehensive input validation for search queries
 * Combines multiple validation techniques
 * 
 * @param input - Search query to validate
 * @param options - Validation options
 * @returns Validated and sanitized search query
 */
export function validateSearchQuery(
  input: string, 
  options: {
    maxLength?: number;
    allowWildcards?: boolean;
    allowEmail?: boolean;
  } = {}
): string {
  const { 
    maxLength = 100, 
    allowWildcards = false, 
    allowEmail = false 
  } = options;
  
  if (!input || typeof input !== 'string') {
    return '';
  }
  
  // Use appropriate sanitizer based on options
  let sanitized = allowEmail 
    ? sanitizeEmailInput(input) 
    : sanitizeSearchInput(input);
  
  // Remove wildcards if not allowed
  if (!allowWildcards) {
    sanitized = sanitized.replace(/[*?]/g, '');
  }
  
  // Apply length limit
  return sanitized.substring(0, maxLength).trim();
}